import React, { useState, useEffect } from 'react';
import './Partner.css';
// Import your images
import silverLogo from '../assets/images/silver_purity.png';
import goldLogo from '../assets/images/pure_gold.webp';
import adv from '../assets/images/didi.svg';
import Logo from '../assets/images/logo.webp';
import gpStore from '../assets/images/google_play_store.png';
import appStoreImg from '../assets/images/app-store.svg';
// Import feature images
import sell from '../assets/images/sell.webp';
import gift from '../assets/images/gift.webp';
import convert from '../assets/images/convert.webp';
import sip from '../assets/images/sip.png';
import Footer from '../components/Footer';

function Partner() {
  const [metalData, setMetalData] = useState(null);
  const [selectedMetal, setSelectedMetal] = useState('GOLD');
  const [weight, setWeight] = useState('');
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(true);
  const [hoveredFeature, setHoveredFeature] = useState(null);
  
  // Modal/Popup State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState(null);

  // Contact Form State
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    mobile: '',
    email: '',
    message: ''
  });

  const [formErrors, setFormErrors] = useState({
    firstName: '',
    lastName: '',
    mobile: '',
    email: '',
    message: ''
  });

  const [formSubmitted, setFormSubmitted] = useState(false);

  // Feature hover texts - ADD THIS BACK
  const featureHoverTexts = {
    gift: 'Send digital gold as a gift to your loved ones',
    convert: 'Convert between different denominations',
    sip: 'Start a Systematic Investment Plan',
    sell: 'Sell your gold anytime at live market rates'
  };

  // Feature data with details for popup
  const featureDetails = {
    gift: {
      title: 'Gift Digital Gold',
      description: 'Send digital gold as a gift to your loved ones on special occasions. It\'s secure, instant, and hassle-free.',
      icon: gift,
      benefits: [
        'Instant delivery to any mobile number',
        'Secure and insured storage',
        'Can be redeemed anytime',
        'Perfect for weddings, birthdays, and festivals'
      ]
    },
    convert: {
      title: 'Convert Gold',
      description: 'Convert between different denominations of gold and silver easily. Swap your investments with zero hassle.',
      icon: convert,
      benefits: [
        'Convert between digital and physical gold',
        'Swap between different purities',
        'Flexible conversion options',
        'Real-time market rates'
      ]
    },
    sip: {
      title: 'SIP in Gold',
      description: 'Start a Systematic Investment Plan in gold and silver. Invest small amounts regularly and build wealth over time.',
      icon: sip,
      benefits: [
        'Start with just ₹500 per month',
        'Rupee cost averaging benefit',
        'Long-term wealth creation',
        'Completely digital and paperless'
      ]
    },
    sell: {
      title: 'Sell Your Gold',
      description: 'Sell your digital or physical gold anytime at live market rates. Get instant cash in your bank account.',
      icon: sell,
      benefits: [
        'Sell anytime 24/7',
        'Get market-best rates',
        'Instant settlement',
        'No hidden charges'
      ]
    }
  };

  // Fetch metal rates from API
  useEffect(() => {
    const fetchMetalRates = async () => {
      try {
        const response = await fetch('https://b2c-api-uat.paulgold.in:8444/api/v1/metal/rates/latest');
        const data = await response.json();
        if (data.success) {
          setMetalData(data.data);
        }
        setLoading(false);
      } catch (error) {
        console.error('Error fetching metal rates:', error);
        setLoading(false);
      }
    };
    fetchMetalRates();
  }, []);

  // Get selected metal data
  const getSelectedMetalData = () => {
    if (!metalData) return null;
    return metalData.find(item => item.metal_type === selectedMetal);
  };

  const metalInfo = getSelectedMetalData();

  // Calculate amount when weight changes
  const handleWeightChange = (e) => {
    const value = e.target.value;
    setWeight(value);

    if (value && metalInfo) {
      const weightInGrams = parseFloat(value);
      if (!isNaN(weightInGrams) && weightInGrams > 0) {
        const buyRate = parseFloat(metalInfo.buy_rate);
        const calculatedAmount = weightInGrams * buyRate;
        setAmount(`₹${calculatedAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
      } else {
        setAmount('');
      }
    } else {
      setAmount('');
    }
  };

  // Get display values
  const getDisplayValues = () => {
    if (!metalInfo) {
      return {
        buyRate: 'Loading...',
        purity: '24K 99.9%',
        metalType: 'GOLD',
        buyRateRaw: 0
      };
    }

    const isGold = metalInfo.metal_type === 'GOLD';
    return {
      buyRate: `₹${parseFloat(metalInfo.buy_rate).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/g`,
      purity: isGold ? '24K 99.9%' : '999',
      metalType: metalInfo.metal_type,
      buyRateRaw: parseFloat(metalInfo.buy_rate)
    };
  };

  const display = getDisplayValues();

  // Handle metal tab switch
  const handleMetalSwitch = (metal) => {
    setSelectedMetal(metal);
    setWeight('');
    setAmount('');
  };

  // Get button text
  const getButtonText = () => {
    return selectedMetal === 'GOLD' ? 'BUY GOLD' : 'BUY SILVER';
  };

  const getSubtext = () => {
    return selectedMetal === 'GOLD'
      ? 'Buy 24K 999 purity guaranteed by PML'
      : 'Buy 999 purity silver guaranteed by PML';
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
  };

  // Validate individual field
  const validateField = (name, value) => {
    let error = '';

    switch (name) {
      case 'firstName':
        if (!value.trim()) {
          error = 'First name is required';
        } else if (value.trim().length < 2) {
          error = 'First name must be at least 2 characters';
        } else if (!/^[a-zA-Z\s]*$/.test(value)) {
          error = 'First name should only contain letters';
        }
        break;

      case 'lastName':
        if (!value.trim()) {
          error = 'Last name is required';
        } else if (value.trim().length < 2) {
          error = 'Last name must be at least 2 characters';
        } else if (!/^[a-zA-Z\s]*$/.test(value)) {
          error = 'Last name should only contain letters';
        }
        break;

      case 'mobile':
        if (!value.trim()) {
          error = 'Mobile number is required';
        } else if (!/^[0-9]{10}$/.test(value.trim())) {
          error = 'Please enter a valid 10-digit mobile number';
        }
        break;

      case 'email':
        if (!value.trim()) {
          error = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) {
          error = 'Please enter a valid email address';
        }
        break;

      case 'message':
        if (!value.trim()) {
          error = 'Message is required';
        } else if (value.trim().length < 10) {
          error = 'Message must be at least 10 characters';
        }
        break;

      default:
        break;
    }

    return error;
  };

  // Validate entire form
  const validateForm = () => {
    const errors = {};
    let isValid = true;

    // Validate all fields
    Object.keys(formData).forEach(key => {
      const error = validateField(key, formData[key]);
      if (error) {
        errors[key] = error;
        isValid = false;
      }
    });

    setFormErrors(errors);
    return isValid;
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    if (validateForm()) {
      // Form is valid - submit data
      console.log('Form submitted:', formData);
      setFormSubmitted(true);

      // Reset form after successful submission
      setTimeout(() => {
        setFormData({
          firstName: '',
          lastName: '',
          mobile: '',
          email: '',
          message: ''
        });
        setFormSubmitted(false);
        alert('Thank you for contacting us! We will get back to you shortly.');
      }, 1000);
    }
  };

  // Open modal with feature details
  const openModal = (featureKey) => {
    setSelectedFeature(featureKey);
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden'; // Prevent scrolling
  };

  // Close modal
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedFeature(null);
    document.body.style.overflow = 'auto'; // Restore scrolling
  };

  // Close modal when clicking outside
  const handleModalClick = (e) => {
    if (e.target.className === 'modal-overlay') {
      closeModal();
    }
  };

  // Handle Escape key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        closeModal();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  // Get feature display name
  const getFeatureDisplayName = (feature) => {
    const names = {
      gift: 'Gift',
      convert: 'Convert',
      sip: 'SIP',
      sell: 'Sell'
    };
    return names[feature] || feature;
  };

  return (
    <div className="page partner-page">
      <div className="partner-hero">
        <div className="container">
          <div className="partner-hero-wrapper">
            <div className="partner-hero-content">
              <p className="partner-description-bold">
                The smartest way to invest in <span className="gold-highlight">24K Digital Gold</span> &amp; Silver.
              </p>
              <p className="partner-features-text">
                Invest in pure 24K gold and silver starting from just ₹10.
                Secure, transparent, and stored in world-class bank vaults.
              </p>
              <div className="partner-buttons">
                <button className="btn-gold">Get Started</button>
                <button className="btn-outline">Learn More</button>
              </div>
              {/* Feature Logos */}
              <div className="partner-features">
                <div className="feature-item">
                  <img src={goldLogo} alt="24K Pure Gold" className="feature-logo" />
                  <div className="feature-text-wrapper">
                    <span className="btn-text-bold">24K</span>
                    <span className="btn-text-light">Pure Gold</span>
                  </div>
                </div>
                <div className="feature-item">
                  <img src={silverLogo} alt="999 Silver Purity" className="feature-logo" />
                  <div className="feature-text-wrapper">
                    <span className="btn-text-bold-silver">999</span>
                    <span className="btn-text-light-silver">Silver Purity</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side - Female Photo */}
           {/* Right Side - Female Photo */}
<div className="partner-hero-image">
  <div className="hero-image-container">
    <img src={adv} alt="Paul Gold Advisor" className="hero-adv-image" />
    <div className="hero-image-line"></div>
  </div>
</div>
          </div>
        </div>
      </div>

      {/* Gold/Silver Access Section */}
      <div className="access-section">
        <div className="container">
          <div className="access-wrapper">
            {/* Left Side - Content */}
            <div className="access-left">
              <div className="access-header">
                <h2 className="access-title">Get access to the safest way of procuring Gold / Silver</h2>
                <p className="access-description">
                  We at DigiGold want to make your gold journey simple, transparent and trustworthy so that you can get the optimum output of your savings.
                </p>
              </div>

              <div className="access-features">
                <div 
                  className={`access-feature-item ${hoveredFeature === 'gift' ? 'active' : ''}`}
                  onMouseEnter={() => setHoveredFeature('gift')}
                  onMouseLeave={() => setHoveredFeature(null)}
                  onClick={() => openModal('gift')}
                >
                  <img src={gift} alt="Gift" className="access-icon-img" />
                  <span className="access-feature-name">Gift</span>
                </div>
                <div 
                  className={`access-feature-item ${hoveredFeature === 'convert' ? 'active' : ''}`}
                  onMouseEnter={() => setHoveredFeature('convert')}
                  onMouseLeave={() => setHoveredFeature(null)}
                  onClick={() => openModal('convert')}
                >
                  <img src={convert} alt="Convert" className="access-icon-img" />
                  <span className="access-feature-name">Convert</span>
                </div>
                <div 
                  className={`access-feature-item ${hoveredFeature === 'sip' ? 'active' : ''}`}
                  onMouseEnter={() => setHoveredFeature('sip')}
                  onMouseLeave={() => setHoveredFeature(null)}
                  onClick={() => openModal('sip')}
                >
                  <img src={sip} alt="SIP" className="access-icon-img" />
                  <span className="access-feature-name">SIP</span>
                </div>
                <div 
                  className={`access-feature-item ${hoveredFeature === 'sell' ? 'active' : ''}`}
                  onMouseEnter={() => setHoveredFeature('sell')}
                  onMouseLeave={() => setHoveredFeature(null)}
                  onClick={() => openModal('sell')}
                >
                  <img src={sell} alt="Sell" className="access-icon-img" />
                  <span className="access-feature-name">Sell</span>
                </div>
              </div>

              {/* Hover Description - Shows below the feature icons */}
              <div className="feature-hover-area">
                {hoveredFeature && (
                  <div className="feature-hover-description-box">
                    <span className="feature-hover-label">{getFeatureDisplayName(hoveredFeature)}</span>
                    <span className="feature-hover-text">{featureHoverTexts[hoveredFeature]}</span>
                  </div>
                )}
              </div>

              <div className="app-download-section">
                <div className="app-buttons">
                  <img src={appStoreImg} alt="App Store" className="app-store-btn" />
                  <img src={gpStore} alt="Google Play Store" className="google-play-btn" />
                </div>
              </div>
            </div>

            {/* Right Side - Price Box */}
            <div className="access-right">
              <div className="access-price-section">
                {/* Metal Tabs */}
                <div className="metal-tabs">
                  <button
                    className={`metal-tab ${selectedMetal === 'GOLD' ? 'active' : ''}`}
                    onClick={() => handleMetalSwitch('GOLD')}
                  >
                    GOLD
                  </button>
                  <button
                    className={`metal-tab ${selectedMetal === 'SILVER' ? 'active' : ''}`}
                    onClick={() => handleMetalSwitch('SILVER')}
                  >
                    SILVER
                  </button>
                </div>

                <div className="price-card">
                  <div className="price-label">
                    Buying Price
                    <span className="price-purity">{display.purity}</span>
                  </div>
                  <div className="price-amount">{display.buyRate}</div>
                  <div className="price-live">Live Price</div>
                </div>

                <div className="price-inputs">
                  <div className="input-group">
                    <label>Weight (gms)</label>
                    <input
                      type="number"
                      placeholder="Enter weight"
                      className="price-input"
                      value={weight}
                      onChange={handleWeightChange}
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div className="input-group">
                    <label>Amount (₹)</label>
                    <input
                      type="text"
                      placeholder="Enter amount"
                      className="price-input"
                      value={amount}
                      readOnly
                    />
                  </div>
                </div>

                <button className="buy-gold-btn">
                  {getButtonText()}
                </button>
                <p className="buy-subtext">
                  {getSubtext()}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ===== MODAL/POPUP ===== */}
      {isModalOpen && selectedFeature && (
        <div className="modal-overlay" onClick={handleModalClick}>
          <div className="modal-content">
            <button className="modal-close-btn" onClick={closeModal}>×</button>
            
            <div className="modal-header">
              <div className="modal-icon-wrapper">
                <img 
                  src={featureDetails[selectedFeature].icon} 
                  alt={featureDetails[selectedFeature].title} 
                  className="modal-icon"
                />
              </div>
              <h2 className="modal-title">{featureDetails[selectedFeature].title}</h2>
            </div>
            
            <p className="modal-description">
              {featureDetails[selectedFeature].description}
            </p>
            
            <div className="modal-benefits">
              <h3 className="modal-benefits-title">Key Benefits:</h3>
              <ul className="modal-benefits-list">
                {featureDetails[selectedFeature].benefits.map((benefit, index) => (
                  <li key={index} className="modal-benefit-item">
                    <span className="modal-benefit-icon">✦</span>
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>
            
            <button className="modal-action-btn" onClick={closeModal}>
              Get Started
            </button>
          </div>
        </div>
      )}

      {/* Contact Us Section with Validation */}
      <div className="contact-section">
        <div className="container">
          <div className="contact-header">
            <h2 className="contact-title">Contact Us</h2>
            <p className="contact-subtitle">Our team will get in touch with you shortly</p>
          </div>

          <form className="contact-form" onSubmit={handleSubmit} noValidate>
            <div className="form-row">
              <div className="form-group">
                <label>First Name <span className="required">*</span></label>
                <input
                  type="text"
                  name="firstName"
                  placeholder="Enter Your First Name"
                  className={`form-input ${formErrors.firstName ? 'error' : ''}`}
                  value={formData.firstName}
                  onChange={handleInputChange}
                  onBlur={(e) => {
                    const error = validateField('firstName', e.target.value);
                    setFormErrors({
                      ...formErrors,
                      firstName: error
                    });
                  }}
                />
                {formErrors.firstName && <span className="error-message">{formErrors.firstName}</span>}
              </div>
              <div className="form-group">
                <label>Last Name <span className="required">*</span></label>
                <input
                  type="text"
                  name="lastName"
                  placeholder="Enter Your Last Name"
                  className={`form-input ${formErrors.lastName ? 'error' : ''}`}
                  value={formData.lastName}
                  onChange={handleInputChange}
                  onBlur={(e) => {
                    const error = validateField('lastName', e.target.value);
                    setFormErrors({
                      ...formErrors,
                      lastName: error
                    });
                  }}
                />
                {formErrors.lastName && <span className="error-message">{formErrors.lastName}</span>}
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Your Mobile Number <span className="required">*</span></label>
                <input
                  type="tel"
                  name="mobile"
                  placeholder="Enter Your Mobile Number"
                  className={`form-input ${formErrors.mobile ? 'error' : ''}`}
                  value={formData.mobile}
                  onChange={handleInputChange}
                  onBlur={(e) => {
                    const error = validateField('mobile', e.target.value);
                    setFormErrors({
                      ...formErrors,
                      mobile: error
                    });
                  }}
                  maxLength="10"
                />
                {formErrors.mobile && <span className="error-message">{formErrors.mobile}</span>}
              </div>

              <div className="form-group">
                <label>Your Email <span className="required">*</span></label>
                <input
                  type="email"
                  name="email"
                  placeholder="Enter Your Email"
                  className={`form-input ${formErrors.email ? 'error' : ''}`}
                  value={formData.email}
                  onChange={handleInputChange}
                  onBlur={(e) => {
                    const error = validateField('email', e.target.value);
                    setFormErrors({
                      ...formErrors,
                      email: error
                    });
                  }}
                />
                {formErrors.email && <span className="error-message">{formErrors.email}</span>}
              </div>
            </div>
            <div className="form-group">
              <label>Your Message <span className="required">*</span></label>
              <textarea
                name="message"
                placeholder="Enter Your Message"
                className={`form-textarea ${formErrors.message ? 'error' : ''}`}
                rows="4"
                value={formData.message}
                onChange={handleInputChange}
                onBlur={(e) => {
                  const error = validateField('message', e.target.value);
                  setFormErrors({
                    ...formErrors,
                    message: error
                  });
                }}
              ></textarea>
              {formErrors.message && <span className="error-message">{formErrors.message}</span>}
            </div>

            <p className="form-disclaimer">
              By submitting this form, you authorize Paul Gold & its associates to call/email/SMS on your registered mobile number/email.
            </p>

            <button type="submit" className="send-btn" disabled={formSubmitted}>
              {formSubmitted ? 'Sending...' : 'Send'}
            </button>
          </form>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default Partner;